import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Link } from 'wouter'
import { ArrowLeft, FileText, Shield, Users, CreditCard } from 'lucide-react'

export function Terms() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 to-primary/10 p-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <Link href="/login">
            <Button variant="ghost" className="mb-4">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Login
            </Button>
          </Link>
          
          <div className="text-center">
            <div className="inline-flex items-center justify-center h-16 w-16 rounded-xl bg-gradient-to-r from-primary to-primary/80 mb-6">
              <FileText className="h-8 w-8 text-white" />
            </div>
            <h1 className="text-4xl font-bold text-foreground mb-4">Terms of Service</h1>
            <p className="text-xl text-muted-foreground">
              Last updated: {new Date().toLocaleDateString()}
            </p>
          </div>
        </div>

        {/* Terms Content */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="text-2xl">1. Acceptance of Terms</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-muted-foreground leading-relaxed">
              By accessing and using SourcedIn ("the Service"), you accept and agree to be bound by the terms and provision of this agreement. 
              If you do not agree to abide by the above, please do not use this service.
            </p>
          </CardContent>
        </Card>

        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="text-2xl">2. Description of Service</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-muted-foreground leading-relaxed">
              SourcedIn is a Request for Proposal (RFP) management platform that connects procurement professionals with suppliers. 
              Our service includes RFP creation, management, response handling, and AI-powered content generation.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
              <div className="flex items-center space-x-3">
                <FileText className="h-5 w-5 text-primary" />
                <span className="text-sm font-medium">RFP Management</span>
              </div>
              <div className="flex items-center space-x-3">
                <Users className="h-5 w-5 text-primary" />
                <span className="text-sm font-medium">Supplier Network</span>
              </div>
              <div className="flex items-center space-x-3">
                <Shield className="h-5 w-5 text-primary" />
                <span className="text-sm font-medium">Secure Platform</span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="text-2xl">3. Subscription Plans</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-muted-foreground leading-relaxed">
              We offer both free and paid subscription plans. Free users can browse RFPs and use basic features, 
              while paid subscribers gain access to advanced features including RFP creation and response capabilities.
            </p>
            <div className="bg-muted/50 rounded-lg p-4">
              <div className="flex items-center space-x-3 mb-2">
                <CreditCard className="h-5 w-5 text-primary" />
                <span className="font-medium">Pro Plan: $199/month</span>
              </div>
              <p className="text-sm text-muted-foreground">
                Includes unlimited RFP creation, AI assistance, advanced analytics, and priority support.
              </p>
            </div>
          </CardContent>
        </Card>

        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="text-2xl">4. User Responsibilities</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-muted-foreground leading-relaxed">
              Users are responsible for maintaining the confidentiality of their account information and for all activities 
              that occur under their account. Users must provide accurate and complete information when creating an account.
            </p>
            <ul className="list-disc list-inside space-y-2 text-muted-foreground">
              <li>Maintain accurate account information</li>
              <li>Protect your login credentials</li>
              <li>Use the service in compliance with applicable laws</li>
              <li>Respect intellectual property rights</li>
            </ul>
          </CardContent>
        </Card>

        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="text-2xl">5. Privacy and Data Protection</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-muted-foreground leading-relaxed">
              We are committed to protecting your privacy and personal data. Our collection and use of personal information 
              is governed by our Privacy Policy, which is incorporated into these Terms of Service by reference.
            </p>
            <Link href="/privacy">
              <Button variant="outline" className="mt-2">
                View Privacy Policy
              </Button>
            </Link>
          </CardContent>
        </Card>

        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="text-2xl">6. Limitation of Liability</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-muted-foreground leading-relaxed">
              SourcedIn shall not be liable for any indirect, incidental, special, consequential, or punitive damages, 
              including without limitation, loss of profits, data, use, goodwill, or other intangible losses.
            </p>
          </CardContent>
        </Card>

        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="text-2xl">7. Contact Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-muted-foreground leading-relaxed">
              If you have any questions about these Terms of Service, please contact us at:
            </p>
            <div className="bg-muted/50 rounded-lg p-4">
              <p className="font-medium">SourcedIn Support</p>
              <p className="text-sm text-muted-foreground">Email: support@sourcedin.com</p>
              <p className="text-sm text-muted-foreground">Phone: +1 (555) 123-4567</p>
            </div>
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="text-center py-8">
          <Link href="/login">
            <Button>
              Return to Login
            </Button>
          </Link>
        </div>
      </div>
    </div>
  )
}
